import { getDateFnsLocale } from '@app-builder/services/i18n/i18n-config';
import { createSimpleContext } from '@app-builder/utils/create-context';
import { formatDuration, useFormatDateTime, useFormatLanguage } from '@app-builder/utils/format';
import { add, sub } from 'date-fns';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Temporal } from 'temporal-polyfill';
import { Calendar, cn, type DateRange } from 'ui-design-system';

import { filtersI18n } from './filters-i18n';

interface StaticDateRangeFilterType {
  type: 'static';
  startDate: string;
  endDate: string;
}

interface DynamicDateRangeFilterType {
  type: 'dynamic';
  fromNow: string;
}

export type DateRangeFilterType = StaticDateRangeFilterType | DynamicDateRangeFilterType | null | undefined;

function adaptStaticDateRangeFilterType({ from, to }: DateRange): StaticDateRangeFilterType {
  const startDate = from?.toISOString() ?? '';
  // Add a day to the end date because the user expects the end date to be included.
  // To fully understand that, think about the special case where the user selects the same day in the Calendar picker (from = to)
  // "From" means the start of the day, and "to" means the end of the day.
  const endDate = to ? add(to, { days: 1 }).toISOString() : '';

  return {
    type: 'static',
    startDate,
    endDate,
  };
}

function adaptDateRange({ startDate, endDate }: StaticDateRangeFilterType): DateRange | undefined {
  const from = startDate ? new Date(startDate) : undefined;
  // look at adaptStaticDateRangeFilterType for the reason why we substract a day
  const to = endDate ? sub(new Date(endDate), { days: 1 }) : undefined;

  return from || to ? { from, to } : undefined;
}

const DateRangeFilterContext = createSimpleContext<{
  fromNow?: string;
  calendarSelected?: DateRange;
  onCalendarSelect: (range?: DateRange) => void;
  onFromNowSelect: (fromNow: string) => void;
}>('DateRangeFilterContext');

const useDateRangeFilterContext = DateRangeFilterContext.useValue;

function DateRangeFilterRoot({
  dateRangeFilter,
  setDateRangeFilter,
  children,
  className,
}: {
  dateRangeFilter: DateRangeFilterType;
  setDateRangeFilter: (dateRangeFilter: DateRangeFilterType) => void;
  children: React.ReactNode;
  className?: string;
}) {
  const calendarSelected = dateRangeFilter?.type === 'static' ? adaptDateRange(dateRangeFilter) : undefined;

  const onCalendarSelect = useCallback(
    (range?: DateRange) => {
      setDateRangeFilter(adaptStaticDateRangeFilterType(range ?? { from: undefined }));
    },
    [setDateRangeFilter],
  );

  const fromNow = dateRangeFilter?.type === 'dynamic' ? dateRangeFilter.fromNow : undefined;
  const onFromNowSelect = useCallback(
    (fromNow: string) => {
      setDateRangeFilter({
        type: 'dynamic',
        fromNow,
      });
    },
    [setDateRangeFilter],
  );

  const value = {
    fromNow,
    calendarSelected,
    onCalendarSelect,
    onFromNowSelect,
  };
  return (
    <DateRangeFilterContext.Provider value={value}>
      <div className={className}>{children}</div>
    </DateRangeFilterContext.Provider>
  );
}

// used as default date range in the DateRangeFilterFromNowPicker
export const last30DaysDuration = Temporal.Duration.from({ days: -30 }).toString();

export const fromNowDurations = [
  Temporal.Duration.from({ days: -7 }).toString(),
  Temporal.Duration.from({ days: -14 }).toString(),
  last30DaysDuration,
  Temporal.Duration.from({ months: -3 }).toString(),
  Temporal.Duration.from({ months: -6 }).toString(),
  Temporal.Duration.from({ months: -12 }).toString(),
] as const;

function isSameFromNow(fromNow: string | undefined, duration: string) {
  if (!fromNow) return false;
  if (fromNow === duration) return true;
  try {
    return Temporal.Duration.from(fromNow).toString() === Temporal.Duration.from(duration).toString();
  } catch {
    return false;
  }
}

function DateRangeFilterFromNowPicker({ title, className }: { title: string; className?: string }) {
  const language = useFormatLanguage();
  const { onFromNowSelect } = useDateRangeFilterContext();
  const { fromNow } = useDateRangeFilterContext();

  return (
    <div className={cn('flex flex-col gap-md p-md', className)}>
      <div className="flex items-center">
        <p className="text-grey-primary text-s font-normal first-letter:capitalize">{title}</p>
      </div>
      <div className="flex flex-col gap-xs">
        {fromNowDurations.map((duration) => (
          <button
            key={duration}
            onClick={() => {
              onFromNowSelect(duration);
            }}
            className={cn(
              'text-s bg-surface-card text-grey-primary border-grey-white dark:border-grey-border flex h-10 items-center rounded-sm border p-sm outline-hidden',
              'hover:bg-purple-background-light active:bg-purple-background hover:text-purple-primary dark:hover:bg-grey-background-light dark:hover:text-purple-hover',
              isSameFromNow(fromNow, duration) &&
                'bg-purple-background border-purple-primary text-purple-primary dark:bg-grey-background-light dark:text-purple-hover',
            )}
          >
            <time dateTime={duration}>{formatDuration(duration, language)}</time>
          </button>
        ))}
      </div>
    </div>
  );
}

function DateRangeFilterCalendar({ className }: { className?: string }) {
  const language = useFormatLanguage();
  const { calendarSelected, onCalendarSelect } = useDateRangeFilterContext();

  return (
    <div className={cn('p-md', className)}>
      <Calendar
        mode="range"
        selected={calendarSelected}
        onSelect={onCalendarSelect}
        defaultMonth={calendarSelected?.from}
        locale={getDateFnsLocale(language)}
      />
    </div>
  );
}

function DateRangeFilterSummary({ className }: { className?: string }) {
  const language = useFormatLanguage();
  const { t } = useTranslation(filtersI18n);
  const { fromNow, calendarSelected } = useDateRangeFilterContext();

  if (fromNow) {
    return (
      <div className={cn('m-md flex h-10 w-full items-center justify-center', className)}>
        <time
          className="text-s text-grey-primary flex h-10 items-center rounded-sm p-sm outline-hidden"
          dateTime={fromNow}
        >
          {t('filters:up_to', {
            duration: formatDuration(fromNow, language),
          })}
        </time>
      </div>
    );
  }

  return (
    <div className={cn('grid grid-cols-[1fr_max-content_1fr] gap-xs p-md', className)}>
      <FormatStaticDate className="justify-self-end" date={calendarSelected?.from} />
      <span className="text-l self-center">→</span>
      <FormatStaticDate date={calendarSelected?.to} />
    </div>
  );
}

function FormatStaticDate({ date, className }: { date?: string | Date; className?: string }) {
  const formatDateTime = useFormatDateTime();

  const dateTime = typeof date === 'string' ? date : date?.toDateString();
  const formattedDate = date ? formatDateTime(date, { dateStyle: 'short' }) : '--/--/----';

  return (
    <time
      dateTime={dateTime}
      className={cn(
        'border-grey-border h-10 w-fit rounded-sm border p-sm',
        date ? 'text-grey-primary' : 'text-grey-secondary',
        className,
      )}
    >
      {formattedDate}
    </time>
  );
}

export const DateRangeFilter = {
  Root: DateRangeFilterRoot,
  FromNowPicker: DateRangeFilterFromNowPicker,
  Calendar: DateRangeFilterCalendar,
  Summary: DateRangeFilterSummary,
};
